import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { toast } from "react-toastify";
import { appendTeamMemberLawyerId } from "../../../../styles/styles";

const createPaginatedUrl = (
  baseUrl,
  page,
  pageSize,
  search,
  searchKey = "case_name",
  queryParams = {},
) => {
  const url = new URL(baseUrl, window.location.origin);
  const params = new URLSearchParams(url.search);

  Object.entries(queryParams || {}).forEach(([key, value]) => {
    if (value === null || value === undefined || value === "") {
      params.delete(key);
      return;
    }

    params.set(key, value);
  });

  if (page !== undefined) {
    params.set("page", page);
  }
  if (pageSize !== undefined) {
    params.set("page_size", pageSize);
  }
  if (search) {
    params.set(searchKey, search);
  }

  url.search = params.toString();
  return url.toString();
};

const formatPaginationData = (responseData, page, pageSize) => {
  return {
    ...responseData,
    pagination: {
      page: page || 1,
      pageSize: pageSize || 10,
      count: responseData.count || 0,
      next: responseData.next,
      previous: responseData.previous,
    },
  };
};

export const getPracticeArea = createAsyncThunk(
  "case/getPracticeArea",
  async ({ url, token }, { rejectWithValue }) => {
    try {
      const response = await axios.get(url, {
        headers: {
          Authorization: `Token ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      toast.error("Failed to get practice areas");
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getCases = createAsyncThunk(
  "case/getCases",
  async (params, { rejectWithValue }) => {
    const {
      url,
      token,
      page,
      pageSize,
      search,
      searchKey = "case_name",
      queryParams,
      teamMemberLawyerId,
      signal,
    } = params;

    try {
      const baseUrl = appendTeamMemberLawyerId(url, teamMemberLawyerId);
      const paginatedUrl = createPaginatedUrl(
        baseUrl,
        page,
        pageSize,
        search,
        searchKey,
        queryParams,
      );

      const response = await axios.get(paginatedUrl, {
        headers: { Authorization: `Token ${token}` },
        signal,
      });

      return formatPaginationData(response.data, page, pageSize);
    } catch (error) {
      if (
        error?.code === "ERR_CANCELED" ||
        error?.name === "CanceledError" ||
        error?.name === "AbortError"
      ) {
        throw error;
      }
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getCaseByid = createAsyncThunk(
  "case/getCaseByid",
  async ({ url, token, teamMemberLawyerId }, { rejectWithValue }) => {
    try {
      const finalUrl = appendTeamMemberLawyerId(url, teamMemberLawyerId);
      const response = await axios.get(finalUrl, {
        headers: { Authorization: `Token ${token}` },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getEditCaseData = createAsyncThunk(
  "getEditCaseData",
  async ({ url, token, teamMemberLawyerId }, { rejectWithValue }) => {
    try {
      const finalUrl = appendTeamMemberLawyerId(url, teamMemberLawyerId);
      const response = await axios.get(finalUrl, {
        headers: { Authorization: `Token ${token}` },
      });
      return response.data;
    } catch (error) {
      toast.error("Failed to get case data for editing");
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const updateCase = createAsyncThunk(
  "updateCase",
  async (
    { url, body, token, method, teamMemberLawyerId },
    { rejectWithValue },
  ) => {
    try {
      const finalUrl = appendTeamMemberLawyerId(url, teamMemberLawyerId);
      const response = await axios[method](finalUrl, body, {
        headers: { Authorization: `Token ${token}` },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const getCaseTemplate = createAsyncThunk(
  "case/getCaseTemplate",
  async (
    { url, token, page, pageSize, search, teamMemberLawyerId },
    { rejectWithValue },
  ) => {
    try {
      let baseUrl = appendTeamMemberLawyerId(url, teamMemberLawyerId);
      const paginatedUrl = createPaginatedUrl(baseUrl, page, pageSize, search);

      const response = await axios.get(paginatedUrl, {
        headers: { Authorization: `Token ${token}` },
      });

      return formatPaginationData(response.data, page, pageSize);
    } catch (error) {
      toast.error("Failed to get case templates");
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getEdtiTempData = createAsyncThunk(
  "case/getEdtiTempData",
  async (
    { url, token, page, pageSize, search, teamMemberLawyerId },
    { rejectWithValue },
  ) => {
    try {
      // For single item fetch, don't use pagination parameters
      let finalUrl = appendTeamMemberLawyerId(url, teamMemberLawyerId);
      if (url.includes("/")) {
        const response = await axios.get(finalUrl, {
          headers: { Authorization: `Token ${token}` },
        });
        return response.data;
      }

      // For list fetch, use pagination
      const paginatedUrl = createPaginatedUrl(finalUrl, page, pageSize, search);
      const response = await axios.get(paginatedUrl, {
        headers: { Authorization: `Token ${token}` },
      });

      return formatPaginationData(response.data, page, pageSize);
    } catch (error) {
      toast.error("Failed to get template data for editing");
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const createCase = createAsyncThunk(
  "case/createCase",
  async ({ url, body, token, teamMemberLawyerId }, { rejectWithValue }) => {
    try {
      const finalUrl = appendTeamMemberLawyerId(url, teamMemberLawyerId);
      const response = await axios.post(finalUrl, body, {
        headers: { Authorization: `Token ${token}` },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const createCaseTemplate = createAsyncThunk(
  "case/createCaseTemplate",
  async ({ url, body, token, teamMemberLawyerId }, { rejectWithValue }) => {
    try {
      const finalUrl = appendTeamMemberLawyerId(url, teamMemberLawyerId);
      const response = await axios.post(finalUrl, body, {
        headers: { Authorization: `Token ${token}` },
      });
      toast.success("Case template created successfully");
      return response.data;
    } catch (error) {
      toast.error("Failed to create case template");
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// Slice
const CaseSlice = createSlice({
  name: "case",
  initialState: {
    caseData: null,
    allCase: null,
    allCaseTemplate: null,
    allPracticeArea: null,
    editCaseData: null,
    editcaseTemplateData: null,
    loading: false,
    error: null,
    casePagination: {
      page: 1,
      pageSize: 10,
      count: 0,
      next: null,
      previous: null,
    },
    practiceAreaPagination: {
      page: 1,
      pageSize: 10,
      count: 0,
      next: null,
      previous: null,
    },
    caseTemplatePagination: {
      page: 1,
      pageSize: 10,
      count: 0,
      next: null,
      previous: null,
    },
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // getPracticeArea
      .addCase(getPracticeArea.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getPracticeArea.fulfilled, (state, action) => {
        state.loading = false;
        state.allPracticeArea = action.payload;
      })
      .addCase(getPracticeArea.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // getCases
      .addCase(getCases.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCases.fulfilled, (state, action) => {
        state.loading = false;
        state.allCase = action.payload.results;
        state.casePagination = {
          ...action.payload.pagination,
          count: action.payload.count,
          next: action.payload.next,
          previous: action.payload.previous,
        };
      })
      .addCase(getCases.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getCaseByid.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCaseByid.fulfilled, (state, action) => {
        state.loading = false;
        state.caseData = action.payload;
      })
      .addCase(getCaseByid.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // getCaseTemplate
      .addCase(getCaseTemplate.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCaseTemplate.fulfilled, (state, action) => {
        state.loading = false;
        state.allCaseTemplate = action.payload.results;
        state.caseTemplatePagination = {
          ...action.payload.pagination,
          count: action.payload.count,
          next: action.payload.next,
          previous: action.payload.previous,
        };
      })
      .addCase(getCaseTemplate.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getEdtiTempData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getEdtiTempData.fulfilled, (state, action) => {
        state.loading = false;
        state.editcaseTemplateData = action.payload;
      })
      .addCase(getEdtiTempData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // createCase
      .addCase(createCase.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createCase.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createCase.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // createCaseTemplate
      .addCase(createCaseTemplate.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createCaseTemplate.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createCaseTemplate.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getEditCaseData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getEditCaseData.fulfilled, (state, action) => {
        state.loading = false;
        state.editCaseData = action.payload;
      })
      .addCase(getEditCaseData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateCase.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCase.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateCase.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const CaseReducer = CaseSlice.reducer;
